import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Card, Kpi, Pill, Section, Btn,
  Table, Th, Td, fmt, DistributionBar,
} from '../components/ui-kit';
import { Activity, Database, ShieldCheck, ScanText } from 'lucide-react';

interface Corpus {
  blind_profiles?: { total: number; real: number; synthetic: number };
  raw_jobs?:       { total: number; real: number; synthetic: number };
  with_embedding?: number;
  missions_v3?: number;
  missions_matched?: number;
  mission_matches?: number;
  audit_chain?: number;
  indexes_on_blind?: string[];
}
interface Fairness {
  corpus_distribution?: Record<string, number>;
  matches_7d?: Record<string, number>;
  wolf_tier?: Record<string, number>;
}
interface AuditEvent {
  seq: number;
  op: string;
  payload?: any;
  hash?: string;
  prev_hash?: string;
  created_at?: string;
}

export default function CommandCenter() {
  const { getToken } = useXyqarAuth();
  const [corpus, setCorpus]     = React.useState<Corpus | null>(null);
  const [fairness, setFairness] = React.useState<Fairness | null>(null);
  const [audit, setAudit]       = React.useState<AuditEvent[]>([]);
  const [err, setErr]           = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const tok = await getToken().catch(() => '');
      const [c, f, a] = await Promise.all([
        apiGet<Corpus>('/api/v1/admin/metrics/corpus', tok),
        apiGet<Fairness>('/api/v1/admin/metrics/fairness', tok),
        apiGet<{ items: AuditEvent[] }>('/api/v1/admin/audit/recent?limit=20', tok),
      ]);
      setCorpus(c); setFairness(f); setAudit(a.items ?? []);
      setErr(null);
    } catch (e: any) { setErr(e.message); }
  }, [getToken]);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000); // refresh every 30 s
    return () => clearInterval(id);
  }, [refresh]);

  const corpusDist = fairness?.corpus_distribution ?? {};
  const distData = ['GREEN', 'YELLOW', 'ORANGE', 'RED'].map((k) => ({ k, v: Number(corpusDist[k] ?? 0) }));

  return (
    <div>
      <PageTitle
        kicker="Ops and trust"
        title="Command center"
        right={
          <div className="flex items-center gap-2">
            <Pill tone="live"><Activity className="h-3 w-3" /> auto-refresh 30s</Pill>
            <Btn onClick={refresh}>Refresh now</Btn>
          </div>
        }
      />

      {err && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <p className="text-amber-300 text-xs font-mono">{err} — some metrics may be stale or restricted by RBAC</p>
        </Card>
      )}

      <Section title="Corpus and evidence base" hint="Operational truth: what exists, what is synthetic, and what can be audited">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Anonymised fiches"
               value={fmt(corpus?.blind_profiles?.total)}
               hint={`${fmt(corpus?.blind_profiles?.real)} real · ${fmt(corpus?.blind_profiles?.synthetic)} synth`} />
          <Kpi accent="blue"  label="Job descriptions"
               value={fmt(corpus?.raw_jobs?.total)}
               hint={`${fmt(corpus?.raw_jobs?.real)} real (FT/Adzuna/Jooble/Reed) · ${fmt(corpus?.raw_jobs?.synthetic)} synth`} />
          <Kpi accent="green" label="With embedding"
               value={fmt(corpus?.with_embedding)}
               hint="384-d MiniLM, pgvector ivfflat" />
          <Kpi accent="green" label="Audit chain depth"
               value={fmt(corpus?.audit_chain)}
               hint="Ed25519 hash-linked" />
        </div>
      </Section>

      <Section title="Match engine" hint="Wolf Brain v3 keeps scoring deterministic and inspectable">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Missions"          value={fmt(corpus?.missions_v3)}        accent="blue" />
          <Kpi label="Matched"           value={fmt(corpus?.missions_matched)}   accent="green" />
          <Kpi label="Persisted matches" value={fmt(corpus?.mission_matches)}    hint="with 8-dim breakdown" />
          <Kpi label="Indexes on blind"  value={fmt(corpus?.indexes_on_blind?.length)} hint="btree + gin + pgvector" />
        </div>
      </Section>

      <Section title="Fairness distribution" hint="See where the persisted match corpus sits before anyone sells a claim">
        <Card>
          <div className="mb-2 flex items-center gap-3">
            <ScanText className="h-4 w-4 text-white/60" />
            <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-mono">Corpus</p>
          </div>
          <DistributionBar data={distData} />
        </Card>
        {fairness?.wolf_tier && Object.keys(fairness.wolf_tier).length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(fairness.wolf_tier).map(([tier, n]) => (
              <Card key={tier}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Wolf tier · {tier}</p>
                <p className="text-2xl text-white mt-2 font-mono">{fmt(n as number)}</p>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Audit chain · live tail"
        hint="Last 20 sealed events from the production audit chain"
        right={<Pill tone="live"><ShieldCheck className="h-3 w-3" /> tamper-evident</Pill>}
      >
        {audit.length === 0 && <Card><p className="text-white/40 text-sm">No audit events visible (RBAC may restrict the bench-bypass token).</p></Card>}
        {audit.length > 0 && (
          <Table>
            <thead>
              <tr><Th>seq</Th><Th>op</Th><Th>when</Th><Th>hash</Th><Th>prev</Th></tr>
            </thead>
            <tbody>
              {audit.map((e) => (
                <tr key={e.seq} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-white/40 font-mono">{e.seq}</span></Td>
                  <Td><span className="text-white text-xs font-mono">{e.op}</span></Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{e.created_at?.slice(0, 19).replace('T', ' ')}</span></Td>
                  <Td><span className="text-[10px] text-white/40 font-mono">{(e.hash ?? '').slice(0, 12)}…</span></Td>
                  <Td><span className="text-[10px] text-white/30 font-mono">{(e.prev_hash ?? '').slice(0, 12)}…</span></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="Why this stack is defensible" hint="The operational controls behind the commercial story">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <Database className="h-5 w-5 text-white/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">EU residency</p>
            <p className="text-white text-sm mt-1">Postgres France Central + West Europe.</p>
          </Card>
          <Card>
            <ShieldCheck className="h-5 w-5 text-white/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Auth</p>
            <p className="text-white text-sm mt-1">Xyqar Identity · RS256 · SAML 2.0 · MFA enforced per tenant.</p>
          </Card>
          <Card>
            <Activity className="h-5 w-5 text-white/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">SLA</p>
            <p className="text-white text-sm mt-1">Bench evidence exists for the runtime path; do not sell more than what the proofs show.</p>
          </Card>
        </div>
      </Section>
    </div>
  );
}
